import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, ArrowRight, X, CaretDown, CaretUp } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Discreet onboarding checklist on the dashboard.
 *
 * Fetches `/api/setup/status` once on mount and renders a compact card ONLY
 * while any of the 5 setup tasks is still incomplete. When everything is
 * done the component collapses itself and stays out of the way.
 *
 * The visual is intentionally small — a light card with progress + expandable
 * task list. It does NOT force a wizard flow; each task links to the standard
 * settings page where the operator (Marin) can configure the account with the
 * client on a call.
 */
export default function SetupAssistant() {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // Dismissable per-session: once user closes the card, don't re-open until refresh
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/setup/status")
      .then((r) => { if (mounted) setStatus(r.data); })
      .catch(() => { /* endpoint optional — fail silent */ });
    return () => { mounted = false; };
  }, []);

  if (!status || status.all_done || dismissed) return null;

  const { tasks, completed, total } = status;
  const pct = Math.round((completed / total) * 100);

  return (
    <div
      className="mb-6 rounded-lg border border-primary/20 bg-primary/[0.04] px-4 py-3"
      data-testid="setup-assistant"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle size={16} weight="duotone" className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-foreground">Postavljanje računa</p>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
              {completed}/{total}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-primary/10">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${pct}%` }}
              data-testid="setup-progress-bar"
            />
          </div>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-primary/10"
          aria-label={expanded ? "Sakrij" : "Prikaži"}
          data-testid="setup-toggle"
        >
          {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-primary/10"
          aria-label="Zatvori"
          data-testid="setup-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {expanded && (
        <ul className="mt-3 space-y-1.5 border-t border-primary/10 pt-3">
          {tasks.map((task) => (
            <li key={task.key}>
              <Link
                to={task.href}
                className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors ${task.done ? "text-muted-foreground" : "text-foreground hover:bg-primary/5"}`}
                data-testid={`setup-task-${task.key}`}
              >
                {task.done ? (
                  <CheckCircle size={16} weight="fill" className="text-primary" />
                ) : (
                  <Circle size={16} className="text-muted-foreground" />
                )}
                <span className={task.done ? "line-through" : "font-medium"}>{task.label}</span>
                {!task.done && (
                  <ArrowRight size={12} className="ml-auto text-primary opacity-60" />
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
